/**
 * Convert a wall-clock time-string in some timezone into the matching
 * UTC `Date`. JS doesn't natively support "parse this string as if it
 * happened in zone X" so we use Intl.DateTimeFormat to compute the
 * offset of `tz` at that moment.
 *
 * Inputs accepted:
 *   - "2026-06-15T14:30"  (datetime-local format)
 *   - "2026-06-15T14:30:00"
 *
 * Example:
 *   tzWallToUtc('2026-06-15T14:30', 'Asia/Kolkata')
 *     → 2026-06-15T09:00:00Z   (IST is UTC+5:30)
 *
 * Pure function, no dependencies. Returns null on unparseable input.
 */
export function tzWallToUtc(wall: string, tz: string): Date | null {
  if (!wall) return null
  const asIfUtc = new Date(wall + (wall.endsWith('Z') ? '' : 'Z'))
  if (Number.isNaN(asIfUtc.getTime())) return null

  // What wall-clock time does `asIfUtc` correspond to in `tz`?
  // The difference between that and `wall` is the offset of `tz` from
  // UTC at this moment — apply it in reverse to get the real UTC.
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(asIfUtc)

  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '00'
  // 'en-US' formatter returns "24" for midnight on some platforms — normalize.
  const hh = get('hour') === '24' ? '00' : get('hour')
  const tzWall = `${get('year')}-${get('month')}-${get('day')}T${hh}:${get('minute')}:${get('second')}`
  const offsetMs = new Date(tzWall + 'Z').getTime() - asIfUtc.getTime()
  return new Date(asIfUtc.getTime() - offsetMs)
}

/**
 * Inverse: take a UTC instant and render it as a wall-clock string
 * suitable for `<input type="datetime-local">` (no timezone suffix).
 * Used to populate edit forms with a value that displays in org-tz.
 */
export function utcToTzWall(utcIso: string | Date, tz: string): string {
  const d = utcIso instanceof Date ? utcIso : new Date(utcIso)
  if (Number.isNaN(d.getTime())) return ''
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  }).formatToParts(d)
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '00'
  const hh = get('hour') === '24' ? '00' : get('hour')
  return `${get('year')}-${get('month')}-${get('day')}T${hh}:${get('minute')}`
}

/**
 * Pretty short tz label for UI hints. Falls back to the raw zone string
 * if the formatter can't extract a name (rare).
 */
export function shortTzLabel(tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, timeZoneName: 'short',
    }).formatToParts(new Date())
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? tz
  } catch {
    return tz
  }
}
